import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@helpers/api';
import { useCommunity } from 'contexts/community';
import { DATE_INTERVAL_PRESETS, toApiDate } from '../enums/dateIntervalPresets';
import { CHARGE_STATUS_MAP } from '../enums/chargeStatuses';
import { PAYMENT_METHOD_MAP } from '../enums/paymentMethods';
import { GRANULARITY_TYPE_MAP } from '../enums/granularityTypes';
import buildMockReport from './mockSalesReport';

const INITIAL_PRESET = DATE_INTERVAL_PRESETS.find(p => p.id === 'last_30_days');
const INITIAL_RANGE = INITIAL_PRESET.resolve();
const DEFAULT_GRANULARITY = { type: 'day', amount: 1 };

export default function useInsights() {
    const { communities, loading: communitiesLoading } = useCommunity();
    const [searchParams, setSearchParams] = useSearchParams();

    const ownedCommunities = useMemo(
        () => (communities || []).filter(c => c.role === 'owner'),
        [communities]
    );

    // Parse URL filters once on first render. We intentionally don't react to
    // later URL changes — the URL is one-way derived from filter state after
    // mount so the page can be shared via link.
    const initialFromUrlRef = useRef(null);
    if (initialFromUrlRef.current === null) {
        initialFromUrlRef.current = parseFiltersFromUrl(searchParams);
    }
    const initialFromUrl = initialFromUrlRef.current;

    // Design/QA mode: `?mock=true` renders the page from a fixed multi-currency
    // fixture instead of the API, so the visualization can be demoed regardless
    // of the account's real sales.
    const isMock = initialFromUrl.mock;

    const [community, setCommunityState] = useState(initialFromUrl.community);
    const [dateRange, setDateRange] = useState(initialFromUrl.dateRange || {
        presetId: INITIAL_PRESET.id,
        from: INITIAL_RANGE.from,
        to: INITIAL_RANGE.to,
    });
    const [planIds, setPlanIds] = useState(initialFromUrl.planIds);
    const [chargeStatuses, setChargeStatuses] = useState(initialFromUrl.chargeStatuses);
    const [chargeMethods, setChargeMethods] = useState(initialFromUrl.chargeMethods);
    const [providerFilters, setProviderFilters] = useState(initialFromUrl.providerFilters);

    // Currency VIEW toggle (BRL national / USD international). Purely client-side:
    // it picks which returned per-currency bucket to display and never re-queries,
    // so the split donut always has both currencies and switching is instant.
    const [selectedCurrency, setSelectedCurrency] = useState(initialFromUrl.selectedCurrency);
    const [granularity, setGranularity] = useState(initialFromUrl.granularity || DEFAULT_GRANULARITY);

    const [plansByCommunity, setPlansByCommunity] = useState({});
    const [plansLoading, setPlansLoading] = useState(false);

    const plans = useMemo(
        () => (community ? plansByCommunity[community] || [] : []),
        [community, plansByCommunity]
    );

    const [report, setReport] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);
    const reportReqIdRef = useRef(0);
    // Ref-backed mirror of plansLoading. React batches setState so reading the
    // state value from within an effect that runs in the same render cycle as
    // loadPlans() sees the stale value. The ref updates synchronously.
    const plansLoadingRef = useRef(false);
    // Gate URL writes and the "default plan ids = all plans" behavior until
    // the first plans-load has finished, so URL-supplied planIds aren't wiped.
    const initialPlansLoadedRef = useRef(false);

    useEffect(() => {
        if (!ownedCommunities.length) return;
        loadAllPlans(ownedCommunities);
    }, [ownedCommunities]);

    useEffect(() => {
        if (!ownedCommunities.length) return;

        // Drop a URL-supplied community that doesn't belong to this user.
        if (community && !ownedCommunities.some(c => c.id === community)) {
            setCommunityState(null);
            return;
        }
        if (community) return;

        const firstWithPlans = ownedCommunities.find(c => (plansByCommunity[c.id] || []).length > 0);
        if (firstWithPlans) {
            setCommunityState(firstWithPlans.id);
        } else if (!plansLoadingRef.current) {
            setCommunityState(ownedCommunities[0].id);
        }
    }, [ownedCommunities, community, plansByCommunity]);

    useEffect(() => {
        if (plansLoading) return;
        if (!community) return;
        if (initialPlansLoadedRef.current) return;
        initialPlansLoadedRef.current = true;

        // First plans-load for the URL-initialized community: keep the URL's
        // planIds (filtered to ones that still exist) or fall back to all.
        // If the community has no plans we leave planIds as the URL value —
        // insights aren't shown for plan-less communities anyway.
        if (!plans.length) return;
        if (initialFromUrl.planIds.length) {
            const valid = initialFromUrl.planIds.filter(id => plans.some(p => p.id === id));
            setPlanIds(valid.length ? valid : plans.map(p => p.id));
        } else {
            setPlanIds(plans.map(p => p.id));
        }
    }, [plansLoading, community, plans]);

    useEffect(() => {
        if (!community || !dateRange?.from || !dateRange?.to) return;
        if (plansLoadingRef.current) return;
        if (!plans.length) return;
        loadReport();
    }, [community, dateRange, planIds, chargeStatuses, chargeMethods, providerFilters, granularity, plansLoading]);

    // Sync filter state → URL. Runs only after the initial plans load so the
    // URL-supplied planIds get a chance to be applied first.
    useEffect(() => {
        if (!initialPlansLoadedRef.current) return;
        setSearchParams(
            serializeFiltersToUrl({
                community,
                dateRange,
                planIds,
                allPlanIds: plans.map(p => p.id),
                chargeStatuses,
                chargeMethods,
                providerFilters,
                granularity,
                selectedCurrency,
                mock: isMock,
            }),
            { replace: true }
        );
    }, [community, dateRange, planIds, chargeStatuses, chargeMethods, providerFilters, granularity, selectedCurrency, plans, isMock]);

    // Memoized so the breadcrumb-action effect (which lists it as a dep) doesn't
    // re-fire every render; only changes when the plan map it reads does.
    const setCommunity = useCallback((newCommunity) => {
        setCommunityState(newCommunity);
        const newPlans = plansByCommunity[newCommunity] || [];
        setPlanIds(newPlans.map(p => p.id));
    }, [plansByCommunity]);

    async function loadAllPlans(communities) {
        plansLoadingRef.current = true;
        setPlansLoading(true);
        try {
            const results = await Promise.all(
                communities.map(c =>
                    api.get(`community/${c.id}/plan`, { include_deleted: true })
                        .then(({ data }) => [c.id, Array.isArray(data) ? sortPlans(data) : []])
                        .catch(() => [c.id, []])
                )
            );
            setPlansByCommunity(Object.fromEntries(results));
        } catch (e) {
            console.log(e);
            setPlansByCommunity({});
        }
        plansLoadingRef.current = false;
        setPlansLoading(false);
    }

    function sortPlans(list) {
        return [...list].sort((a, b) => {
            const aDeleted = !!a.deleted_at;
            const bDeleted = !!b.deleted_at;
            if (aDeleted !== bDeleted) return aDeleted ? 1 : -1;
            return (a.label || '').localeCompare(b.label || '');
        });
    }

    async function loadReport() {
        const reqId = ++reportReqIdRef.current;

        // `?mock=true`: skip the API and hand the view the fixture directly, using
        // the community's real plans so the per-plan chart keeps true labels.
        if (isMock) {
            setReport(buildMockReport(plans));
            setReportLoading(false);
            return;
        }

        setReportLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('date_from', toApiDate(dateRange.from));
            params.append('date_to', toApiDate(dateRange.to));
            params.append('granularity_type', granularity.type);
            params.append('granularity_amount', granularity.amount);
            const planIdsToSend = planIds.length ? planIds : plans.map(p => p.id);
            planIdsToSend.forEach(id => params.append('plan_ids[]', id));
            chargeStatuses.forEach(s => params.append('charge_statuses[]', s));
            chargeMethods.forEach(m => params.append('charge_methods[]', m));
            providerFilters.forEach(p => params.append('provider[]', p));

            const { data } = await api.get(`community/${community}/insights/salesReport?${params.toString()}`);
            if (reqId !== reportReqIdRef.current) return;
            setReport(normalizeSalesReport(data));
        } catch (e) {
            if (reqId !== reportReqIdRef.current) return;
            console.log(e);
            setReport(null);
        }
        if (reqId === reportReqIdRef.current) {
            setReportLoading(false);
        }
    }

    function applyPreset(presetId) {
        const preset = DATE_INTERVAL_PRESETS.find(p => p.id === presetId);
        if (!preset) return;
        const { from, to } = preset.resolve();
        setDateRange({ presetId, from, to });
    }

    function applyCustomRange(from, to) {
        if (!from || !to) return;
        setDateRange({ presetId: 'custom', from, to });
    }

    function clearOptionalFilters() {
        setPlanIds(plans.map(p => p.id));
        setChargeStatuses([]);
        setChargeMethods([]);
        setProviderFilters([]);
    }

    return {
        communitiesLoading,
        ownedCommunities,
        community,
        setCommunity,

        dateRange,
        applyPreset,
        applyCustomRange,

        plans,
        plansByCommunity,
        plansLoading,
        planIds,
        setPlanIds,

        chargeStatuses,
        setChargeStatuses,

        chargeMethods,
        setChargeMethods,

        providerFilters,
        setProviderFilters,

        granularity,
        setGranularity,

        clearOptionalFilters,

        selectedCurrency,
        setSelectedCurrency,
        availableCurrencies: report?.availableCurrencies || ['BRL'],

        report,
        reportLoading,
    };
}

function parseFiltersFromUrl(searchParams) {
    return {
        community: parseCommunity(searchParams.get('community')),
        dateRange: parseDateRange(searchParams),
        planIds: parseIdList(searchParams.get('plans'), true),
        chargeStatuses: parseIdList(searchParams.get('statuses'), false)
            .filter(id => CHARGE_STATUS_MAP[id]),
        chargeMethods: parseIdList(searchParams.get('methods'), false)
            .filter(id => PAYMENT_METHOD_MAP[id]),
        providerFilters: parseIdList(searchParams.get('providers'), false)
            .filter(id => ['asaas', 'stripe'].includes(id)),
        granularity: parseGranularity(searchParams.get('granularity')),
        selectedCurrency: parseCurrencyView(searchParams.get('currency')),
        mock: searchParams.get('mock') === 'true',
    };
}

function parseCurrencyView(raw) {
    const cur = String(raw || '').toUpperCase();
    return ['BRL', 'USD'].includes(cur) ? cur : 'BRL';
}

function parseCommunity(raw) {
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
}

function parseDateRange(searchParams) {
    const presetId = searchParams.get('preset');
    if (!presetId) return null;
    if (presetId === 'custom') {
        const from = parseDateOnly(searchParams.get('from'), false);
        const to = parseDateOnly(searchParams.get('to'), true);
        if (!from || !to) return null;
        return { presetId: 'custom', from, to };
    }
    const preset = DATE_INTERVAL_PRESETS.find(p => p.id === presetId);
    if (!preset) return null;
    const { from, to } = preset.resolve();
    return { presetId, from, to };
}

function parseGranularity(raw) {
    if (!raw) return null;
    const [type, amountStr] = raw.split(':');
    if (!GRANULARITY_TYPE_MAP[type]) return null;
    const amount = amountStr ? parseInt(amountStr, 10) : 1;
    if (Number.isNaN(amount) || amount < 1) return null;
    return { type, amount };
}

function parseIdList(raw, numeric) {
    if (!raw) return [];
    return raw.split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .map(s => numeric ? parseInt(s, 10) : s)
        .filter(v => !numeric || !Number.isNaN(v));
}

function parseDateOnly(str, isEnd) {
    if (!str) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (!m) return null;
    const [, y, mo, d] = m;
    const date = new Date(+y, +mo - 1, +d, isEnd ? 23 : 0, isEnd ? 59 : 0, isEnd ? 59 : 0, 0);
    return Number.isNaN(date.getTime()) ? null : date;
}

function toDateOnly(d) {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    const pad = n => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function serializeFiltersToUrl({
    community,
    dateRange,
    planIds,
    allPlanIds,
    chargeStatuses,
    chargeMethods,
    providerFilters,
    granularity,
    selectedCurrency,
    mock,
}) {
    const params = {};

    if (mock) params.mock = 'true';
    if (community != null) params.community = String(community);

    if (dateRange?.presetId && dateRange.presetId !== INITIAL_PRESET.id) {
        params.preset = dateRange.presetId;
        if (dateRange.presetId === 'custom') {
            params.from = toDateOnly(dateRange.from);
            params.to = toDateOnly(dateRange.to);
        }
    }

    // Only include plans when the selection isn't the implicit "all" default.
    if (allPlanIds.length && !sameIdSet(planIds, allPlanIds)) {
        params.plans = planIds.join(',');
    }

    if (chargeStatuses.length) params.statuses = chargeStatuses.join(',');
    if (chargeMethods.length) params.methods = chargeMethods.join(',');
    if (providerFilters.length) params.providers = providerFilters.join(',');
    if (selectedCurrency && selectedCurrency !== 'BRL') params.currency = selectedCurrency;

    if (granularity.type !== DEFAULT_GRANULARITY.type || granularity.amount !== DEFAULT_GRANULARITY.amount) {
        params.granularity = granularity.amount > 1
            ? `${granularity.type}:${granularity.amount}`
            : granularity.type;
    }

    return params;
}

// Sections that arrive under a per-currency `byCurrency` map (WP-13 / M12).
const REPORT_SECTIONS = ['summary', 'methods', 'salesAmount', 'revenue', 'productRevenue'];
// National first, international next, then anything else alphabetically.
const CURRENCY_ORDER = ['BRL', 'USD'];

// The API nests a `byCurrency` map *inside every section* (summary/methods/the
// three time series) so BRL and USD cents are never summed. Flatten that into a
// list of self-contained per-currency reports the metrics + charts can consume
// directly: each carries its own summary/methods and time series with the shared
// granularity re-attached. A legacy flat payload (no byCurrency anywhere) is kept
// working as a single BRL report so a mid-rollout backend doesn't blank the page.
function normalizeSalesReport(data) {
    if (!data) return null;

    const currencySet = new Set();
    let sawByCurrency = false;
    for (const section of REPORT_SECTIONS) {
        const map = data[section]?.byCurrency;
        if (map && typeof map === 'object' && !Array.isArray(map)) {
            sawByCurrency = true;
            Object.keys(map).forEach(cur => currencySet.add(String(cur).toUpperCase()));
        }
    }

    if (!sawByCurrency) {
        return {
            ...data,
            availableCurrencies: ['BRL'],
            currencyReports: [{ currency: data.currency || 'BRL', ...data }],
        };
    }

    const currencies = Array.from(currencySet).sort(compareCurrencies);

    const bucket = (section, currency) => {
        const map = data[section]?.byCurrency;
        if (!map || typeof map !== 'object') return undefined;
        // Keys are emitted uppercase; look up defensively either way.
        return map[currency] ?? map[currency.toLowerCase()];
    };

    const series = (section, currency) => ({
        granularity: data[section]?.granularity,
        data: bucket(section, currency)?.data || [],
    });

    const currencyReports = currencies.map(currency => ({
        currency,
        summary: bucket('summary', currency) || null,
        methods: bucket('methods', currency) || null,
        salesAmount: series('salesAmount', currency),
        revenue: series('revenue', currency),
        productRevenue: series('productRevenue', currency),
    }));

    return {
        ...data,
        availableCurrencies: currencies,
        currencyReports,
    };
}

function compareCurrencies(a, b) {
    const ia = CURRENCY_ORDER.indexOf(a);
    const ib = CURRENCY_ORDER.indexOf(b);
    if (ia !== -1 || ib !== -1) {
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    }
    return a.localeCompare(b);
}

function sameIdSet(a, b) {
    if (a.length !== b.length) return false;
    const set = new Set(a);
    return b.every(id => set.has(id));
}
